import putDocumentToS3 from '@/utils/putDocumentToS3';
import sha256Base64 from '@/utils/sha256Base64';

interface CreateDocumentParams {
  fileName: string;
  contentType: string;
  contentSha256: string;
}

interface CreatedDocument {
  uploadUrl: string;
  documentUuid: string;
}

/**
 * Shared register -> PUT to S3 -> confirm flow behind every SEA document-upload mutation.
 * `createDocument` and `completeDocument` are caller-supplied so each hook can hit its own
 * endpoint/DTO shape (and wrap its own errors); this only owns the parts identical across all of
 * them: hashing the file, PUTting it to S3, and failing before confirming if that PUT fails.
 */
const uploadDocumentViaS3 = async <TComplete>(
  file: File,
  createDocument: (params: CreateDocumentParams) => Promise<CreatedDocument>,
  completeDocument: (documentUuid: string, size: number) => Promise<TComplete>
): Promise<{ documentUuid: string; completeResult: TComplete }> => {
  const contentType = file.type || 'application/octet-stream';
  const contentSha256 = await sha256Base64(file);

  const { uploadUrl, documentUuid } = await createDocument({ fileName: file.name, contentType, contentSha256 });

  const putResponse = await putDocumentToS3(uploadUrl, file, contentType, contentSha256);

  if (!putResponse.ok) {
    throw new Error(`Failed to upload document to S3 [status=${putResponse.status}]`);
  }

  const completeResult = await completeDocument(documentUuid, file.size);

  return { documentUuid, completeResult };
};

export default uploadDocumentViaS3;
